import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Activity,
  Clock,
  Code,
  Database,
  Globe,
  HardDrive,
  Cpu,
  MemoryStick,
  FolderTree,
  Loader2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import { formatRelativeTime, formatBytes, formatDate } from '@/utils/formatters';
import { projectsApi } from '@/api/projects';
import type { Project, ProjectStats } from '@/types';

interface ProjectStatus {
  projectStatus: string;
  containers: Array<{
    name: string;
    status: string;
    health?: string;
  }>;
}

export function ProjectOverviewPage() {
  const { project } = useOutletContext<{ project: Project }>();
  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [containerStatus, setContainerStatus] = useState<ProjectStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsData, statusData] = await Promise.all([
          projectsApi.getStats(project.slug).catch(() => null),
          projectsApi.getStatus(project.slug).catch(() => null),
        ]);
        setStats(statsData);
        setContainerStatus(statusData);
      } catch (error) {
        console.error('Failed to fetch project data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [project.slug]);

  // Poll for status updates every 10 seconds for real-time container health
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const statusData = await projectsApi.getStatus(project.slug).catch(() => null);
        if (statusData) {
          setContainerStatus(statusData);
        }
      } catch (error) {
        // Silently ignore polling errors
      }
    };

    // Start polling after initial load
    const intervalId = setInterval(pollStatus, 10000);

    return () => clearInterval(intervalId);
  }, [project.slug]);

  return (
    <div className="h-full p-6 space-y-6 overflow-y-auto">
      {/* Project Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <InfoRow label="Name" value={project.name} />
              <InfoRow label="Slug" value={project.slug} />
              <InfoRow
                label="Description"
                value={project.description || 'No description'}
              />
              <InfoRow label="Stack" value={project.stackType || 'Default'} />
              <InfoRow
                label="Database"
                value={project.hasDatabase ? 'PostgreSQL' : 'None'}
              />
              <InfoRow label="Visibility" value={project.visibility} />
              <InfoRow
                label="Created"
                value={formatDate(project.createdAt)}
              />
              <InfoRow
                label="Last Updated"
                value={formatRelativeTime(project.updatedAt)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Current Status</span>
              <StatusBadge status={containerStatus?.projectStatus || project.status} />
            </div>

            {containerStatus?.containers && containerStatus.containers.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm text-gray-500 dark:text-gray-400 block">
                  Containers
                </span>
                {containerStatus.containers.map((container, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{container.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      container.status === 'running'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {container.status}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {project.previewUrl && (
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400 block mb-2">
                  Preview URL
                </span>
                <a
                  href={project.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 break-all"
                >
                  {project.previewUrl}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Code className="w-5 h-5" />}
          label="Files"
          value={loading ? null : (stats?.fileCount?.toString() || '0')}
          loading={loading}
        />
        <StatCard
          icon={<FolderTree className="w-5 h-5" />}
          label="Directories"
          value={loading ? null : (stats?.directoryCount?.toString() || '0')}
          loading={loading}
        />
        <StatCard
          icon={<HardDrive className="w-5 h-5" />}
          label="Total Size"
          value={loading ? null : formatBytes(stats?.totalSize || 0)}
          loading={loading}
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="Last Modified"
          value={loading ? null : (stats?.lastModified ? formatRelativeTime(stats.lastModified) : 'N/A')}
          loading={loading}
        />
      </div>

      {/* Resource Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Resource Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ResourceMeter
              icon={<Cpu className="w-5 h-5" />}
              label="CPU"
              value={project.status === 'Running' ? 15 : 0}
              max={100}
              unit="%"
            />
            <ResourceMeter
              icon={<MemoryStick className="w-5 h-5" />}
              label="Memory"
              value={project.status === 'Running' ? 256 : 0}
              max={512}
              unit="MB"
              formatValue={(v) => formatBytes(v * 1024 * 1024)}
            />
            <ResourceMeter
              icon={<HardDrive className="w-5 h-5" />}
              label="Storage"
              value={(stats?.totalSize || 0) / (1024 * 1024 * 1024)}
              max={5}
              unit="GB"
            />
          </div>
        </CardContent>
      </Card>

      {/* Endpoints */}
      {project.previewUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Endpoints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <EndpointRow
                label="Preview URL"
                url={project.previewUrl}
              />
              {project.hasDatabase && (
                <EndpointRow
                  label="API URL"
                  url={`${project.previewUrl}/api`}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-dark-700 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

function EndpointRow({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-dark-700 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
      >
        {url}
      </a>
    </div>
  );
}

function ResourceMeter({
  icon,
  label,
  value,
  max,
  unit,
  formatValue,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  max: number;
  unit: string;
  formatValue?: (value: number) => string;
}) {
  const percentage = (value / max) * 100;
  const displayValue = formatValue ? formatValue(value) : `${value.toFixed(1)}${unit}`;
  const displayMax = formatValue ? formatValue(max) : `${max}${unit}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          {icon}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {displayValue} / {displayMax}
        </span>
      </div>
      <Progress
        value={percentage}
        variant={percentage > 80 ? 'danger' : percentage > 60 ? 'warning' : 'default'}
      />
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  loading = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  loading?: boolean;
}) {
  return (
    <Card className="text-center">
      <div className="flex flex-col items-center gap-2 p-4">
        <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400">
          {icon}
        </div>
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        ) : (
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{value}</span>
        )}
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      </div>
    </Card>
  );
}
